/**
 * R-33 — single source of truth for whether a site needs SED bootstrap.
 *
 * Callers (wizard mount, createSite, generation pipeline) consult this
 * before scheduling `bootstrapSedCoordinated` so redundant Firestore /
 * provider work is not queued when the existing SED already matches
 * intent, uploads, and freshness.
 */
import type { ProjectIntent } from '../../types/siteRag';

/** Intent-like payload — accepts full ProjectIntent or SED-stamped subsets. */
export type IntentLike = ProjectIntent | Record<string, unknown> | null | undefined;

/** Default staleness window — rebuild when SED is older than this. */
export const DEFAULT_SED_BOOTSTRAP_STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000;

export interface UploadsSnapshot {
  /** ISO timestamp of the most recent upload mutation on the site. */
  lastChangedAt?: string | null;
}

export interface SedBootstrapSnapshot {
  /** ISO build timestamp (`collectedAt`, `lastBootstrapAt`, or explicit `builtAt`). */
  builtAt?: string | null;
  /** Intent fingerprint stamped at build time (R-33). */
  builtForIntentHash?: string | null;
  builtForStudyAreaHash?: string | null;
  builtForGenerationAssessmentHash?: string | null;
  /** Economic/workforce/research source revision frozen into the SED. */
  builtForEconomicEvidenceRevision?: string | null;
}

export interface ShouldBootstrapOptions {
  nowMs?: number;
  staleThresholdMs?: number;
  /** Revision derived from the canonical site's current economic snapshots. */
  economicEvidenceRevision?: string | null;
  studyAreaHash?: string | null;
  generationAssessmentHash?: string | null;
}

/** Stable fingerprint for project intent — order-independent JSON hash. */
export function hashProjectIntent(intent: IntentLike): string {
  if (!intent) return 'none';
  // Recommendation/evaluate mode changes the UI question, not any physical
  // evidence input. Keep it in decision/CAS identity, but do not collect again
  // merely because the user switches this presentation preference.
  const { screeningMode: _screeningMode, ...evidenceIntent } = intent;
  return fnv1aHex(stableStringify(evidenceIntent));
}

/**
 * Shared intent-stamp currency predicate. Every freshness shortcut and pinned
 * admission consults this one rule, so a coarse "recent build" or an exact
 * pointer pin can never hide intent or promoted-scenario drift (a promoted
 * scenario id/hash change is an intent change: see ProjectIntent).
 *
 * `stamps` are build-time `hashProjectIntent` stamps of the RAW site intent
 * (the site mirror `sedBuiltForIntentHash` and/or the SED body's
 * `builtForIntentHash`). Missing (null/undefined) stamps are ignored; every
 * PRESENT stamp must equal the current hash. With no stamp at all the legacy
 * rule of `shouldBootstrapReason` applies: current only when the intent
 * hashes to 'none' (no intent), so an unstamped SED never vouches for an
 * intent it cannot have been built for.
 */
export function sedIntentStampsCurrent(
  intent: IntentLike,
  stamps: ReadonlyArray<string | null | undefined>,
): boolean {
  const current = hashProjectIntent(intent);
  const present = stamps.filter((stamp): stamp is string => stamp != null);
  if (present.length === 0) return current === 'none';
  return present.every(stamp => stamp === current);
}

/** Max `uploadedAt` across user docs (library-index rows excluded). */
export function resolveUploadsLastChangedAt(
  documents: ReadonlyArray<{ uploadedAt?: string; tags?: string[] }> | null | undefined,
): string | null {
  if (!documents?.length) return null;
  let max = '';
  for (const doc of documents) {
    if ((doc.tags ?? []).includes('library-index')) continue;
    const at = doc.uploadedAt;
    if (at && at > max) max = at;
  }
  return max || null;
}

/** Resolve SED build time from explicit field or legacy stamps. */
export function resolveSedBuiltAt(
  currentSed: SedBootstrapSnapshot | null | undefined,
): number | null {
  const raw = currentSed?.builtAt;
  if (!raw) return null;
  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Why a bootstrap consult concluded "work is needed". Precedence matters:
 * intent drift (which changes what the COMPILE produces) outranks
 * uploads-changed (which the in-place doc/top-up path can fold without a
 * full provider re-fan-out + sedId re-key), which outranks pure age.
 */
export type ShouldBootstrapReason =
  | 'no-sed'
  | 'intent-drift'
  | 'economic-evidence-drift'
  | 'study-area-drift'
  | 'generation-assessment-drift'
  | 'uploads-changed'
  | 'stale-age';

/**
 * Reason-carrying variant of `shouldBootstrap` — the routing seam for
 * consult sites that can answer 'uploads-changed' with the ADDITIVE
 * in-place top-up (`topUpSiteSed` refreshes `uploadedDocuments` and
 * re-runs `applyDocAwareConfidence`) instead of a full rebuild. A full
 * rebuild re-keys the sedId, which strands decision analyses bound under
 * hash scheme v1 and breaks frozen Test Fit authority chains — never the
 * right response to "a document was uploaded".
 */
export function shouldBootstrapReason(
  _siteId: string,
  intent: IntentLike,
  uploads: UploadsSnapshot,
  currentSed: SedBootstrapSnapshot | null | undefined,
  opts?: ShouldBootstrapOptions,
): ShouldBootstrapReason | false {
  if (!currentSed?.builtAt) return 'no-sed';
  if ((opts?.studyAreaHash ?? null) !== (currentSed.builtForStudyAreaHash ?? null)) return 'study-area-drift';
  if ((opts?.generationAssessmentHash ?? null) !== (currentSed.builtForGenerationAssessmentHash ?? null)) return 'generation-assessment-drift';

  // A present stamp must equal hash(intent). Legacy SED without stamp — the
  // shared predicate treats a missing hash as drift when intent exists.
  if (!sedIntentStampsCurrent(intent, [currentSed.builtForIntentHash])) return 'intent-drift';

  const liveEconomicRevision = opts?.economicEvidenceRevision ?? null;
  const builtEconomicRevision = currentSed.builtForEconomicEvidenceRevision ?? null;
  if (
    liveEconomicRevision != null
    && liveEconomicRevision !== 'economic-evidence:none'
    && builtEconomicRevision !== liveEconomicRevision
  ) {
    return 'economic-evidence-drift';
  }
  if (
    builtEconomicRevision != null
    && builtEconomicRevision !== 'economic-evidence:none'
    && liveEconomicRevision === 'economic-evidence:none'
  ) {
    return 'economic-evidence-drift';
  }

  const builtAtMs = resolveSedBuiltAt(currentSed);
  if (builtAtMs == null) return 'no-sed';

  const uploadAt = uploads.lastChangedAt;
  if (uploadAt) {
    const uploadMs = Date.parse(uploadAt);
    if (Number.isFinite(uploadMs) && uploadMs > builtAtMs) return 'uploads-changed';
  }

  const nowMs = opts?.nowMs ?? Date.now();
  const thresholdMs = opts?.staleThresholdMs ?? DEFAULT_SED_BOOTSTRAP_STALE_THRESHOLD_MS;
  if (builtAtMs < nowMs - thresholdMs) return 'stale-age';

  return false;
}

/**
 * Returns true when bootstrap should run for the site.
 *
 * Conditions (any):
 *   a) no current SED snapshot
 *   b) intent hash drift vs build stamp
 *   c) uploads changed after SED build
 *   d) SED older than stale threshold (default 24h)
 */
export function shouldBootstrap(
  siteId: string,
  intent: IntentLike,
  uploads: UploadsSnapshot,
  currentSed: SedBootstrapSnapshot | null | undefined,
  opts?: ShouldBootstrapOptions,
): boolean {
  return shouldBootstrapReason(siteId, intent, uploads, currentSed, opts) !== false;
}

function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return `{${keys.map(k => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(',')}}`;
}

/** 32-bit FNV-1a — deterministic, no crypto dependency. */
function fnv1aHex(input: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/** Build a SedBootstrapSnapshot from loaded SED + site fallbacks. */
export function sedBootstrapSnapshotFrom(
  sed: {
    collectedAt?: string;
    builtAt?: string;
    builtForIntentHash?: string;
    builtForGenerationAssessmentHash?: string;
    studyAreaScope?: { selectionHash: string };
    economicEvidence?: { data?: { sourceRevision?: string } | null };
    projectIntent?: IntentLike;
    fullProjectIntent?: IntentLike;
  } | null | undefined,
  siteFallback?: {
    lastBootstrapAt?: string;
    sedComputedAt?: string;
    /** Site-doc mirror of the SED's build-time intent hash (server stamp). */
    sedBuiltForIntentHash?: string;
    sedBuiltForStudyAreaHash?: string | null;
    sedBuiltForGenerationAssessmentHash?: string;
    sedBuiltForEconomicEvidenceRevision?: string;
  },
): SedBootstrapSnapshot | null {
  const builtAt = sed?.builtAt
    ?? sed?.collectedAt
    ?? siteFallback?.lastBootstrapAt
    ?? siteFallback?.sedComputedAt
    ?? null;
  if (!builtAt) return null;
  // Hash-source preference: the SED's own stamp, then the site-doc mirror,
  // then a hash of the embedded FULL intent (verbatim copy of the intent the
  // build consumed — comparable to hashProjectIntent(live intent)). The
  // SPARSE `sed.projectIntent` view is the last resort ONLY: it is a derived
  // subset whose hash can never equal a full-intent hash, so it reads as
  // permanent drift — acceptable for one converging rebuild on truly legacy
  // SEDs, catastrophic as a primary source (the 2026 re-key storm).
  return {
    builtAt,
    builtForStudyAreaHash: sed?.studyAreaScope?.selectionHash ?? siteFallback?.sedBuiltForStudyAreaHash ?? null,
    builtForGenerationAssessmentHash: sed?.builtForGenerationAssessmentHash ?? siteFallback?.sedBuiltForGenerationAssessmentHash ?? null,
    builtForIntentHash: sed?.builtForIntentHash
      ?? siteFallback?.sedBuiltForIntentHash
      ?? (sed?.fullProjectIntent ? hashProjectIntent(sed.fullProjectIntent) : null)
      ?? (sed?.projectIntent ? hashProjectIntent(sed.projectIntent) : null),
    builtForEconomicEvidenceRevision:
      sed?.economicEvidence?.data?.sourceRevision
      ?? siteFallback?.sedBuiltForEconomicEvidenceRevision
      ?? null,
  };
}
